/**
 * T027/US5 (FR-017, SC-007, 008 FR-018). Follows `../invoicing/GstSummary.tsx` verbatim as the
 * pattern for a from/to-bounded, unpaged, client-exported report: a period form driving
 * `searchParams`, fetch-on-submit-or-bookmarked-load, `ExportButton` fed rows mapped to
 * plainly-keyed objects in the exact header order (`flattenObject`'s `for...in` key-order hazard —
 * see `GstSummary.tsx`'s own comment — is exactly what SC-007 guards against).
 *
 * `GET /reports/stock-movement` returns the **same rows** `GET /stock/{skuId}/movements` returns,
 * over a period and optionally across every SKU in the warehouse (backend's own doc comment on
 * `ReportController.GetStockMovementReport`) — there is no separate report computation to trust.
 * The server defaults `from`=`to`-30d when omitted and refuses (400) `from > to` or a period longer
 * than the live `Warehouse:MaxReportPeriodDays` setting; both refusals render verbatim through
 * `ErrorSurface`, never pre-validated here (FR-034/FR-041 — it is configuration, not a constant).
 *
 * No Reports index exists in `apps/admin` yet (008-reporting hasn't landed here) — this route is
 * reached from the "Stock movement report" nav entry and a link on `Exceptions.tsx`/`Detail.tsx`;
 * add it to 008's Reports index once that page exists, rather than building an index shell here.
 */
import { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { Button } from "rizzui";
import { WidgetCard, ErrorSurface, FieldError, EmptyState, ExportButton } from "@steelyard/theme";
import { SkuPicker } from "../../components/catalog-pickers";
import { DateField } from "../../components/date-field";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatDate, formatMoment, parseIsoDate, toIsoDate } from "../../lib/format";
import { getStockMovementReport, movementKindLabel, movementSourceLabel, personName, reasonCodeLabel, skuLabel, type StockMovementReportView, type StockMovementRow } from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";
import { stockMovementReportColumns } from "./stock-movement-columns";


/*
 * The rows render through `ReportTable` — paged in memory rather than `PagedTable` for the same
 * export-guard reason as `Variance.tsx`'s sections; the export maps every row, not the visible page.
 * `key={report.producedAt}` (unique per response) starts each fetch's table from its first page.
 */

export default function StockMovementReportPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<StockMovementReportView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const skuId = searchParams.get("skuId") ?? "";
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);
  const [skuPick, setSkuPick] = useState(skuId);
  const [periodError, setPeriodError] = useState<string>();

  // Rules-of-hooks: called unconditionally, ahead of the permission-gate return below — same
  // reason GstSummary.tsx's own effect comes before its gate.
  useEffect(() => {
    if (from && to) run(from, to, skuId);
  }, [from, to, skuId]);

  if (!can(permissionState, "inventory.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The stock movement report is gated by inventory.read, which your role does not hold." }} action="view the stock movement report" />;
  }

  async function run(nextFrom: string, nextTo: string, nextSkuId: string) {
    setLoading(true);
    setError(null);
    try {
      setReport(await getStockMovementReport({ from: nextFrom, to: nextTo, skuId: nextSkuId || undefined }));
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!fromDate || !toDate) return setPeriodError("Choose both a From and a To date.");
    setPeriodError(undefined);
    const next: Record<string, string> = { from: fromDate, to: toDate };
    if (skuPick) next.skuId = skuPick;
    // The effect above fetches when the period or SKU changes; re-running the same one is the only
    // case it can't see, so only that one fetches here — otherwise every submit fetched twice.
    if (fromDate === from && toDate === to && skuPick === skuId) run(fromDate, toDate, skuPick);
    else setSearchParams(next);
  }

  const header = (
    <ListHeader
      title="Stock Movement Report"
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Movement Report" }]}
      description={
        <>
          Every receipt, decrement, adjustment, reversal and reservation change in this warehouse over a period — the same
          rows a SKU&rsquo;s own <Link to="/inventory" className="underline">movement history</Link> shows, exported exactly as
          displayed.
        </>
      }
    >
        {report && !report.empty ? (
          <ExportButton
            // Mapped in the EXACT displayed column order, not passed as StockMovementRow[] directly
            // — see GstSummary.tsx's own comment on flattenObject's for...in key-order hazard.
            data={report.rows.map((r) => ({
              When: formatMoment(r.occurredAt),
              SKU: skuLabel(r),
              Cause: movementKindLabel(r.kind),
              Source: [movementSourceLabel(r.sourceKind), r.sourceLabel].filter(Boolean).join(" "),
              "Quantity change": r.qtyDelta,
              "Reserved change": r.reservedDelta,
              By: r.actorId ? personName(r.actorName) : "",
              Reason: r.reasonCode ? reasonCodeLabel(r.reasonCode) : "",
            }))}
            header="When,SKU,Cause,Source,Quantity change,Reserved change,By,Reason"
            fileName={`stock-movement-${from}-to-${to}`}
          />
        ) : null}
    </ListHeader>
  );

  const periodForm = (
    <WidgetCard title="Period" rounded="lg" headerClassName="border-muted border-b pb-4">
      <form onSubmit={handleSubmit} noValidate className="flex flex-wrap items-end gap-4 pt-4">
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="From…"
          selected={parseIsoDate(fromDate || null)}
          maxDate={parseIsoDate(toDate || null) ?? undefined}
          onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
        />
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="To…"
          selected={parseIsoDate(toDate || null)}
          minDate={parseIsoDate(fromDate || null) ?? undefined}
          onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
        />
        <div className="w-full sm:w-72">
          {/* Optional: blank means every SKU in the warehouse. */}
          <SkuPicker value={skuPick} onChange={setSkuPick} clearable />
        </div>
        <Button type="submit" disabled={loading}>{loading ? "Running…" : "Run the report"}</Button>
        {periodError ? <div className="basis-full"><FieldError message={periodError} /></div> : null}
      </form>
    </WidgetCard>
  );

  if (!from || !to) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {periodForm}
        <EmptyState title="Choose a period">Bounded to a range on purpose — the server refuses a period longer than its configured maximum rather than running an unbounded export.</EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      {periodForm}

      {error ? <ErrorSurface error={error} action="run the stock movement report" /> : null}

      {report ? (
        report.empty ? (
          <WidgetCard
            title={<>{formatDate(from)} to {formatDate(to)} · {report.rows.length} {report.rows.length === 1 ? "row" : "rows"}</>}
            rounded="lg"
            headerClassName="border-muted border-b"
          >
            <EmptyState title="No movements in this period">
              {report.emptyReason ? reasonCodeLabel(report.emptyReason) + "." : "Nothing was recorded in the chosen range."}
            </EmptyState>
          </WidgetCard>
        ) : (
          <ReportTable<StockMovementRow>
            key={report.producedAt}
            title={<>{formatDate(from)} to {formatDate(to)} · {report.rows.length} {report.rows.length === 1 ? "row" : "rows"}</>}
            rows={report.rows}
            columns={stockMovementReportColumns}
            getRowId={(row) => row.id}
            caption="Stock movements across the warehouse for the chosen period, with cause, source, quantity and reason"
            searchPlaceholder="Search movements…"
          />
        )
      ) : null}
    </div>
  );
}
